// Trial status tag for accounts on a free trial (old: src/components/subscription/TrialBadge.tsx).
// Blue for the run, amber inside the warning window, red on the last day and after it lapses.
// Nothing for paid plans, while loading, or on error. Icon only below `md`, the label in its tooltip.
import { CrownIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { BILLING_ENABLED } from '@constants/features';
import { pageUrl } from '@constants/url';
import useSubscription from '@hooks/subscription/use-subscription';
import { deriveTrialStatus } from '@utils/trial-status';
import { Badge } from '@/components/lumen/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { useLanguage } from '@/contexts/LanguageContext';

export function TrialBadge() {
  const { t } = useLanguage();
  const { data, loading, error } = useSubscription();

  if (loading || error || !data) return null;
  const trial = deriveTrialStatus(data);
  if (!trial.isTrial) return null;

  const label = trial.ended
    ? t('subscription.trial.header.ended')
    : trial.daysLeft === 0
      ? t('subscription.trial.header.endsToday')
      : t('subscription.trial.header.daysLeft').replace('{n}', String(trial.daysLeft));
  const variant = trial.ended || trial.daysLeft === 0 ? 'err' : trial.endsSoon ? 'warn' : 'info';

  const content = (
    <>
      <CrownIcon aria-hidden />
      <span className="sr-only md:not-sr-only">{label}</span>
    </>
  );

  // Builds without the billing area redirect /subscription to the dashboard (App.tsx), so the tag is
  // a plain status there rather than a dead link.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {BILLING_ENABLED ? (
          <Badge asChild variant={variant} data-testid="trial-badge" className="h-6 px-2">
            <Link to={pageUrl.subscription}>{content}</Link>
          </Badge>
        ) : (
          // Focusable so keyboard users reach the tooltip when the label is visually hidden.
          <Badge variant={variant} data-testid="trial-badge" className="h-6 px-2" tabIndex={0}>
            {content}
          </Badge>
        )}
      </TooltipTrigger>
      <TooltipContent side="bottom" className="md:hidden">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
